import { ref } from 'vue'
import { TransformLearningProgressService } from '../services/TransformLearningProgressService'
import { useTransformLearningStore } from '../stores/TransformLearningStore'
import { useTransformOwnerContext } from './useTransformOwnerContext'
import { handleErrors } from '../utils/errorHandler'

// Report to telemetry only after this many consecutive save failures, so a transient
// blip stays silent but a genuinely broken save path becomes observable (without
// interrupting playback or spamming Sentry on every retry).
const SAVE_FAILURE_REPORT_THRESHOLD = 3

// A lesson counts as complete once cumulative unique coverage reaches this share.
// Kept below 100% because trailing credits / imprecise timeupdate rarely reach a
// clean 100%.
const DEFAULT_COMPLETE_THRESHOLD = 95

// When the video plays through to its natural end, a lower coverage floor is enough
// to complete — this lets a user who reached the end (having watched most of it)
// finish, while still blocking "seek straight to the end" (coverage stays low).
const DEFAULT_END_COVERAGE_FLOOR = 85

// Persist after roughly this many newly-watched unique seconds accumulate, so a
// long watch produces periodic saves instead of one write per timeupdate tick.
const DEFAULT_FLUSH_INTERVAL_SECONDS = 15

// Safety cap on stored segments to bound the document size under pathological seeking.
const MAX_SEGMENTS = 500

function clampPercent(value) {
  if (typeof value !== 'number' || Number.isNaN(value)) return 0
  return Math.min(100, Math.max(0, Math.round(value)))
}

/**
 * Tracks and persists a lesson's watch progress as cumulative unique coverage.
 *
 * Only whole seconds the player actually plays through are counted (Vimeo does not
 * emit timeupdate for regions the user seeks past), so skipping ahead never advances
 * progress. Coverage is persisted as merged [{start,end}] segments and re-seeded on
 * load, so watching different parts across separate sessions ADDS UP toward true
 * coverage (and completion) instead of resetting each session.
 *
 * `lastPositionSeconds` is tracked separately and used only to resume playback.
 *
 * Must be called from a component setup (the underlying service uses inject).
 *
 * @param {Object} opts
 * @param {string} opts.lessonId
 * @param {string} opts.courseId
 * @param {number} opts.durationSeconds - total video length; coverage is a share of it.
 * @param {number} [opts.initialPercent] - legacy saved % (floor for docs without segments).
 * @param {boolean} [opts.initialCompleted]
 * @param {number} [opts.initialPositionSeconds] - saved playhead position, used to seed resume.
 * @param {Array<{start:number,end:number}>} [opts.initialSegments] - previously watched segments.
 * @param {number} [opts.completeThreshold]
 * @param {number} [opts.endedCoverageFloor] - coverage needed to complete on reaching the end.
 * @param {number} [opts.flushIntervalSeconds]
 */
export function useLessonProgress({
  lessonId,
  courseId,
  durationSeconds,
  initialPercent = 0,
  initialCompleted = false,
  initialPositionSeconds = 0,
  initialSegments = [],
  completeThreshold = DEFAULT_COMPLETE_THRESHOLD,
  endedCoverageFloor = DEFAULT_END_COVERAGE_FLOOR,
  flushIntervalSeconds = DEFAULT_FLUSH_INTERVAL_SECONDS
}) {
  const progressService = TransformLearningProgressService()
  const store = useTransformLearningStore()

  const durationCap = durationSeconds && durationSeconds > 0 ? Math.ceil(durationSeconds) : null
  // Legacy floor: only honored for docs saved BEFORE segment tracking existed (no
  // watchedSegments at all). Once segments are present, they are the single source
  // of truth for coverage — a stale/corrupted uniquePercentWatched from an earlier
  // session must never override the real, verifiable coverage derived from segments.
  const hasInitialSegments = Array.isArray(initialSegments) && initialSegments.length > 0
  const floorPercent = hasInitialSegments ? 0 : clampPercent(initialPercent)

  // Union of whole-second marks actually played through, accumulated ACROSS sessions
  // by seeding from the persisted watched segments.
  const watchedSeconds = new Set()
  seedFromSegments(initialSegments)

  const uniquePercentWatched = ref(computePercent())
  const completed = ref(initialCompleted === true)

  // Last playhead position (seconds) — the resume point. Seeded so leaving without
  // playing doesn't overwrite the saved position with 0.
  let lastPositionSeconds = Math.max(0, Number(initialPositionSeconds) || 0)

  let lastPersistedPercent = uniquePercentWatched.value
  let lastPersistedPositionSeconds = lastPositionSeconds
  let lastFlushWatchedCount = watchedSeconds.size
  let isFlushing = false
  // Set once the video reaches its natural end (Vimeo `ended` event).
  let endReached = false
  // Consecutive save failures — used to escalate to telemetry once, not per retry.
  let consecutiveSaveFailures = 0

  // Adds every integer second within each persisted segment to the coverage set.
  function seedFromSegments(segments) {
    if (!Array.isArray(segments)) return
    for (const seg of segments) {
      let start = Math.floor(Number(seg?.start))
      let end = Math.floor(Number(seg?.end))
      if (Number.isNaN(start) || Number.isNaN(end)) continue
      start = Math.max(0, start)
      if (durationCap !== null) end = Math.min(durationCap, end)
      for (let s = start; s <= end; s++) watchedSeconds.add(s)
    }
  }

  // Compresses the coverage set into merged [{start,end}] integer-second segments.
  function serializeSegments() {
    const secs = [...watchedSeconds].sort((a, b) => a - b)
    const segments = []
    for (const s of secs) {
      const last = segments[segments.length - 1]
      if (last && s <= last.end + 1) last.end = s
      else segments.push({ start: s, end: s })
    }
    return segments.slice(0, MAX_SEGMENTS)
  }

  function computePercent() {
    if (!durationSeconds || durationSeconds <= 0) return floorPercent
    const fromCoverage = Math.round((watchedSeconds.size / durationSeconds) * 100)
    // Monotonic: never below the legacy floor, capped at 100.
    return Math.min(100, Math.max(floorPercent, fromCoverage))
  }

  // Called on every Vimeo `timeupdate`. Marks the current second as watched, tracks
  // the resume position, and triggers a throttled flush as coverage grows.
  function recordTime(seconds) {
    if (seconds === null || seconds === undefined || Number.isNaN(seconds)) return
    const before = watchedSeconds.size
    watchedSeconds.add(Math.floor(seconds))
    // Track the live playhead as the resume point (follows seeks too).
    lastPositionSeconds = seconds

    const pct = computePercent()
    if (pct !== uniquePercentWatched.value) uniquePercentWatched.value = pct

    if (import.meta.env.DEV && watchedSeconds.size !== before && watchedSeconds.size % 5 === 0) {
      console.log(
        '[TLP] recordTime',
        seconds,
        '| coverage=',
        watchedSeconds.size,
        '| duration=',
        durationSeconds,
        '| pct=',
        pct
      )
    }

    if (watchedSeconds.size - lastFlushWatchedCount >= flushIntervalSeconds) {
      flush()
    }
  }

  // Persists progress only when coverage advanced, completion flipped, or the resume
  // position moved — avoids redundant writes. Safe to call often.
  async function flush() {
    // Admin "view-as": the save endpoint derives the user from the TOKEN, so a flush here
    // would write the admin's OWN progress while they watch someone else's lesson. The
    // whole context is read-only — drop every persistence attempt at this single choke
    // point (covers timeupdate throttling, ended, and unmount flushes alike).
    if (useTransformOwnerContext().isActive.value) return
    if (isFlushing) return

    const pct = computePercent()
    // Complete when fully covered, OR when the video reached its end with most of it
    // covered (lets an honest end-reacher finish; skipping to the end stays low-coverage).
    const nowCompleted =
      completed.value || pct >= completeThreshold || (endReached && pct >= endedCoverageFloor)
    const advanced = pct > lastPersistedPercent
    const completionChanged = nowCompleted !== completed.value
    const positionChanged = Math.abs(lastPositionSeconds - lastPersistedPositionSeconds) >= 3

    if (import.meta.env.DEV) {
      console.log('[TLP] flush', {
        pct,
        lastPersistedPercent,
        advanced,
        completionChanged,
        positionChanged,
        endReached,
        position: Math.round(lastPositionSeconds),
        coverage: watchedSeconds.size,
        duration: durationSeconds,
        lessonId,
        courseId
      })
    }

    if (!advanced && !completionChanged && !positionChanged) return

    isFlushing = true
    lastFlushWatchedCount = watchedSeconds.size
    const positionSecondsRounded = Math.round(lastPositionSeconds)
    const watchedSegments = serializeSegments()
    try {
      await progressService.saveLessonProgress({
        lessonId,
        courseId,
        uniquePercentWatched: pct,
        // Single source of truth for progress = cumulative coverage (not the playhead).
        percentComplete: pct,
        watchedSegments,
        lastPositionSeconds: positionSecondsRounded,
        completed: nowCompleted
      })
      if (import.meta.env.DEV)
        console.log('[TLP] saved ✓', {
          pct,
          position: positionSecondsRounded,
          segments: watchedSegments.length,
          completed: nowCompleted
        })
      lastPersistedPercent = pct
      lastPersistedPositionSeconds = lastPositionSeconds
      completed.value = nowCompleted
      consecutiveSaveFailures = 0
      // Mirror the saved state into the store so cards/progress bars update live and a
      // return within the same session re-seeds coverage from these segments.
      store.applyLessonProgress(lessonId, {
        uniquePercentWatched: pct,
        percentComplete: pct,
        watchedSegments,
        lastPositionSeconds: positionSecondsRounded,
        completed: nowCompleted
      })
    } catch (_error) {
      if (import.meta.env.DEV) console.error('[TLP] save FAILED', _error)
      // Keep the UI silent so playback is never interrupted (the next flush retries),
      // but escalate to telemetry once the failures are no longer transient.
      consecutiveSaveFailures += 1
      if (consecutiveSaveFailures === SAVE_FAILURE_REPORT_THRESHOLD) {
        handleErrors(
          `useLessonProgress.flush-${lessonId}`,
          _error,
          { logToConsole: false }
        )
      }
    } finally {
      isFlushing = false
    }
  }

  // Called from the player's `ended` event — the video played through to the end.
  function markEnded() {
    endReached = true
    flush()
  }

  return {
    uniquePercentWatched,
    completed,
    recordTime,
    flush,
    markEnded
  }
}
